/* eslint-disable @typescript-eslint/no-explicit-any */
import { createApi } from "@reduxjs/toolkit/query/react";
import baseQueryWithReauth from "./BaseQuery";

export const orderApi = createApi({
  reducerPath: "orderApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Orders", "dashboard", "PaymentHistory", "ShopifyOrders"],
  endpoints: (builder) => ({
    fetchOrders: builder.query({
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }: any) => ({ type: "Orders", id })),
              "Orders",
            ]
          : ["Orders"],
      query: ({
        query,
        track,
        couriers,
        page,
        limit,
        status,
        payment_status,
        dest,
        bookingFrom,
        bookingTo,
        deliveredTo,
        bookedBy,
        deliveredFrom,
        days,
        op,
        tag,
        phone,
      }) => {
        return `/get-all-orders?query=${query || ""}&city=${
          dest || ""
        }&status=${status || ""}&payment_status=${
          payment_status || ""
        }&bookingDateFrom=${bookingFrom || ""}&bookingDateTo=${
          bookingTo || ""
        }&deliveredDateFrom=${deliveredFrom || ""}&deliveredDateTo=${
          deliveredTo || ""
        }&trackingNumber=${track || ""}&company=${
          couriers || ""
        }&page=${page}&limit=${
          Number(limit) || 50
        }&days=${days}&op=${op}&tag=${tag}&phone=${phone}&bookedBy=${bookedBy}`;
      },
    }),
    trackOrder: builder.query({
      query: (id) => `/get-order-from-shopify/${id}`,
      transformResponse: (resp: any) => resp.orderData,
      // invalidatesTags: ["Orders", "dashboard"],
    }),
    fetchOrdersForMerge: builder.query({
      query: ({ ordersIdsArray }) => {
        return `/get-orders-from-shopify?id=${
          ordersIdsArray?.length > 0 ? ordersIdsArray?.join(",") : ""
        }`;
      },
      transformResponse: (resp: any) => resp,
      keepUnusedDataFor: 1,
    }),
    deleteOrder: builder.mutation({
      query: (id) => ({
        url: `/delete-order`,
        method: "DELETE",
        body: { orderId: id },
      }),
      invalidatesTags: ["Orders", "dashboard"],
    }),
    bookOrder: builder.mutation({
      query: (orderDetails) => {
        return {
          url: `/book-order`,
          method: "POST",
          body: orderDetails,
        };
      },
      invalidatesTags: ["Orders", "dashboard", "ShopifyOrders"],
    }),
    bookOrders: builder.mutation({
      query: (orderDetails) => {
        return {
          url: `/book-orders`,
          method: "POST",
          body: {
            ...orderDetails,
            order_id: orderDetails.order_id
              ? orderDetails?.order_id?.trim()?.split(",")
              : [],
            order_name: orderDetails.order_name
              ? orderDetails?.order_name?.trim()?.split(",")
              : [],
          },
        };
      },
      invalidatesTags: ["Orders", "dashboard", "ShopifyOrders"],
    }),
    cancelBooking: builder.mutation({
      query: (trackingNumber) => ({
        url: `/cancel-booking/${trackingNumber}`,
        method: "PUT",
      }),
      invalidatesTags: ["Orders", "dashboard", "ShopifyOrders"],
    }),
    exportOrders: builder.mutation({
      query: ({ search, filter }) => ({
        url: `/export-orders${search}&filter=${filter}`,
        method: "POST",
      }),
    }),
    importOrders: builder.mutation({
      query: ({ ordersData, fileName }) => ({
        url: `/import-orders`,
        body: {
          ordersData,
          fileName,
        },
        method: "POST",
      }),
      invalidatesTags: ["Orders", "dashboard", "PaymentHistory"],
    }),
    bulkFulfillment: builder.mutation({
      query: ({ ordersData }) => ({
        url: `/bulk/orders/fulfillment`,
        body: {
          ordersData,
        },
        method: "POST",
      }),
      invalidatesTags: ["Orders", "dashboard"],
    }),
    trackOrderSearch: builder.mutation({
      query: (body: { order_name: string }) => ({
        url: `/protected-tracking/search`,
        method: "POST",
        body, // pass directly
      }),
    }),
    getBulkOrdersFromShopify: builder.query({
      query: ({ cursor, direction = "next", query = "" }: { cursor?: string; direction?: "next" | "previous"; query?: string }) => {
        const params = new URLSearchParams();
        if (query && query.trim()) {
          params.set("query", query.trim());
        }
        if (cursor) {
          params.append(direction === "next" ? "after" : "before", cursor);
        }
        const queryString = params.toString();
        return `/get-bulk-orders-from-shopify${queryString ? `?${queryString}` : ""}`;
      },
      transformResponse: (resp: any) => {
        // Transform the response to extract orders from data array
        const orders = resp?.data?.map((item: any) => item.node) || [];
        return {
          orders,
          pageInfo: resp?.pageInfo || {},
        };
      },
      providesTags: ["ShopifyOrders"],
    }),
    aiFilterGenerator: builder.mutation({
      query: (body: { userInput: string; currentDate: string }) => ({
        url: `/ai/filter-generator`,
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useFetchOrdersQuery,
  useTrackOrderQuery,
  useFetchOrdersForMergeQuery,
  useDeleteOrderMutation,
  useBookOrderMutation,
  useBookOrdersMutation,
  useCancelBookingMutation,
  useExportOrdersMutation,
  useImportOrdersMutation,
  useBulkFulfillmentMutation,
  useTrackOrderSearchMutation,
  useGetBulkOrdersFromShopifyQuery,
  useAiFilterGeneratorMutation,
} = orderApi;
